import { useCallback, useState } from 'react';

import { formatCompact, type Sample } from '../../model';
import { ChartFrame, type ChartLine } from '../ChartFrame';
import { useLab } from '../LabContext';
import { PassiveThreatParams } from '../ScenarioPanel';
import { ScaleToggle } from '../controls';
import { PageLink } from '../router';

const LINES: ChartLine[] = [
  {
    key: 'passive',
    label: 'Threat / min',
    color: '#ffd166',
    accessor: (s: Sample) => s.threatPerMinute,
  },
];

function PassiveInfo() {
  const { ruleset, comparing } = useLab();

  if (comparing) {
    return (
      <>
        <p>
          Threat earned per minute, with nothing fed. Live is linear in <b>evolution</b>, so it
          flattens as the evolution curve does; the test branch is linear in <b>total mutagen
          fed</b>, so it never bends.
        </p>
        <p>
          <b>The two are not in the same unit</b> — the test branch prices one small biter at 1
          threat where live prices a behemoth at 38.5. Compare what the income buys, not the income.
        </p>
      </>
    );
  }

  if (!ruleset.hasInstantThreat) {
    return (
      <>
        <p>
          Threat earned per minute, linear in the <b>total mutagen ever fed</b> rather than in
          evolution — so a flask is worth the same income at 300% as at 30%. With instant threat
          removed, this is the only source of threat in the game.
        </p>
        <p>
          One point of threat here is one small biter, not live's 1/38.5 of a behemoth, which is why
          the numbers dwarf live's.
        </p>
      </>
    );
  }

  return (
    <>
      <p className="formula">bb_threat_income = 25 × E, added every second</p>
      <p>
        1,500 × E per minute — strictly linear in evolution and unaffected by player count. It is
        why evolution keeps mattering long after each extra flask has stopped moving it: the stock
        it feeds never stops accruing.
      </p>
    </>
  );
}

export function PassiveThreatChart() {
  const { ruleset, comparing, tuning } = useLab();
  // Linear, like every other row — a chart whose axis type depends on what is
  // selected changes shape under you when you toggle a branch, and reading four
  // rows against one x axis is hard enough without two of them being in a
  // different space than they were a click ago.
  //
  // The cost is real while comparing: the test branch earns an order of
  // magnitude more per minute than live, so live's curve is pressed towards
  // zero. Log is one click away, and only offered there — on one branch there
  // is nothing to squash.
  const [scaleType, setScaleType] = useState<'linear' | 'log'>('linear');
  const effectiveScale = comparing ? scaleType : 'linear';

  const yDomain = useCallback(
    (samples: Sample[]): [number, number] => {
      if (!samples.length) return [0, 1];
      const max = samples[samples.length - 1].threatPerMinute;
      if (effectiveScale === 'log') {
        // Income is 0 at mutagen 0, which a log axis has no room for.
        const positive = samples.find((s) => s.threatPerMinute > 0);
        return [positive ? positive.threatPerMinute : 1, Math.max(max, 10)];
      }
      return [samples[0].threatPerMinute, max];
    },
    [effectiveScale],
  );

  return (
    <ChartFrame
      index={3}
      title="Passive threat"
      info={<PassiveInfo />}
      infoMore={
        <PageLink page="threat" section="illustrator">
          What this income turns into
        </PageLink>
      }
      lines={LINES}
      yDomain={yDomain}
      yScaleType={effectiveScale}
      formatY={(value) => formatCompact(value, 1)}
      // The branches count threat in different units, so while comparing the
      // axis cannot claim either one. The (i) says why.
      yLabel={comparing ? 'per min' : ruleset.hasInstantThreat ? 'thr/min' : 'biters/min'}
      controls={comparing ? <ScaleToggle value={scaleType} onChange={setScaleType} /> : undefined}
      params={tuning ? <PassiveThreatParams /> : undefined}
    />
  );
}
